import { useEffect, useState, useMemo } from "react";
import {
    Box,
    Grid,
    Button,
    Typography,
} from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useSnackBar } from "../FlowBuilder/Common/SnackBar/index.tsx";
import {
    DecisionBoardActions,
    ManagementActions,
} from "../../Services/_actions/index.js";

import SearchableDropdown from "../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx";
import { useNavigate } from "react-router";

export default function StepBusinessCategory({
    formData,
    updateData,
    onNext,
    onBack,
}) {
    const [errors, setErrors] = useState({});
    const [sector, setSector] = useState([]);
    const [endCustomerOptions, setEndCustomerOptions] =
        useState([]);
    const { showSnackBar } = useSnackBar();
    const actions = ManagementActions();
    const action = DecisionBoardActions();
    const navigate = useNavigate();



    const getSector = async () => {
        try {
            const res =
                await actions.getAllBusinessTypeSubTypes();

            if (res?.status === 200) {
                setSector(res?.data || []);
            }
        } catch {
            showSnackBar(
                "Error fetching business type sub types",
                "error"
            );
        }
    };

    const fetchCategory = async () => {
        try {
            const res =
                await action.getTypeOFEndCustomer();

            if (res?.status === 200 && res?.data) {
                const mappedOptions = res.data.map(
                    (item) => ({
                        label: item?.name,
                        value: item?.name,
                    })
                );

                setEndCustomerOptions(
                    mappedOptions
                );
            }
        } catch (error) {
            console.error(
                "Error fetching end customers",
                error
            );
        }
    };

    useEffect(() => {
        getSector();
        fetchCategory();
    }, []);

    useEffect(() => {
        if (
            formData?.categoryData ||
            !formData?.category ||
            !endCustomerOptions.length
        ) {
            return;
        }

        const matchedCategory =
            endCustomerOptions.find(
                (item) =>
                    item.value ===
                    formData.category
            ) || null;

        if (matchedCategory) {
            updateData({
                categoryData:
                    matchedCategory,
            });
        }
    }, [
        endCustomerOptions,
        formData?.category,
        formData?.categoryData,
        updateData,
    ]);

    const selectedSector = useMemo(() => {
        return (
            formData?.sectorData ||
            sector.find(
                (item) =>
                    item.id ===
                    formData?.sectorId ||
                    item.business_type ===
                    formData?.sector
            ) ||
            null
        );
    }, [
        sector,
        formData?.sector,
        formData?.sectorId,
        formData?.sectorData,
    ]);

    const subSectorOptions =
        selectedSector?.subtypes || [];

    useEffect(() => {
        if (
            !selectedSector ||
            formData?.sectorData
        ) {
            return;
        }

        const nextFields = {
            sectorData: selectedSector,
        };

        if (formData?.subSector) {
            const selectedSubSector =
                selectedSector?.subtypes?.find(
                    (item) =>
                        item.id ===
                        formData?.subSectorId ||
                        item.business_sub_type ===
                        formData?.subSector
                ) || null;

            nextFields.subSectorData =
                selectedSubSector;

            if (
                selectedSubSector &&
                !formData?.subSectorId
            ) {
                nextFields.subSectorId =
                    selectedSubSector.id;
            }
        }

        if (!formData?.sectorId) {
            nextFields.sectorId =
                selectedSector.id;
        }

        updateData(nextFields);
    }, [
        selectedSector,
        formData?.sectorData,
        formData?.subSector,
        formData?.subSectorId,
        formData?.sectorId,
        updateData,
    ]);

    const handleSectorChange = (
        selectedOption
    ) => {
        updateData({
            sector:
                selectedOption
                    ?.business_type || "",
            sectorId:
                selectedOption?.id || "",
            sectorData:
                selectedOption || null,

            subSector: "",
            subSectorId: "",
            subSectorData: null,
        });

        setErrors((prev) => ({
            ...prev,
            sector: "",
        }));
    };

    const handleSubSectorChange = (
        selectedOption
    ) => {
        updateData({
            subSector:
                selectedOption
                    ?.business_sub_type || "",
            subSectorId:
                selectedOption?.id || "",
            subSectorData:
                selectedOption || null,
        });
    };

    const handleCategoryChange = (
        selectedOption
    ) => {
        updateData({
            category:
                selectedOption?.value || "",
            categoryData:
                selectedOption || null,
        });

        setErrors((prev) => ({
            ...prev,
            category: "",
        }));
    };

    const handleNext = () => {
        const newErrors = {};

        if (!formData?.sector) {
            newErrors.sector =
                "Please select a sector";
        }

        if (!formData?.category) {
            newErrors.category =
                "Please select a category";
        }

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        onNext();
    };

    return (
        <Box>
            {/* Title */}
            <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
                letterSpacing="0.1em"
            >
                Step 2 of 4
            </Typography>

            <Typography
                variant="h5"
                fontWeight={700}
                mt={0.5}
                mb={0.5}
            >
                Classify your{" "}
                <Box
                    component="span"
                    color="primary.main"
                >
                    business
                </Box>
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                mb={3}
            >
                Help us understand your sector so
                we can tailor AI outputs for your
                industry.
            </Typography>

            {/* Dropdowns */}
            <Box mt={2}>
                <Grid container spacing={2}>
                    {/* Sector */}
                    <Grid item xs={12} sm={4}>
                        <SearchableDropdown
                            label="Sector *"
                            options={sector}
                            value={
                                formData?.sectorData ||
                                null
                            }
                            onChange={
                                handleSectorChange
                            }
                            labelKey="business_type"
                            valueKey="id"
                            size="small"
                            error={
                                !!errors?.sector
                            }
                            errorMessage={
                                errors?.sector
                            }
                        />
                    </Grid>

                    {/* Sub-sector */}
                    <Grid item xs={12} sm={4}>
                        <SearchableDropdown
                            label="Sub-sector"
                            options={
                                subSectorOptions
                            }
                            value={
                                formData?.subSectorData ||
                                null
                            }
                            onChange={
                                handleSubSectorChange
                            }
                            labelKey="business_sub_type"
                            valueKey="id"
                            size="small"
                            disabled={
                                !formData?.sector
                            }
                        />
                    </Grid>

                    {/* Category */}
                    <Grid item xs={12} sm={4}>
                        <SearchableDropdown
                            label="Category *"
                            options={
                                endCustomerOptions
                            }
                            value={
                                formData?.categoryData ||
                                null
                            }
                            onChange={
                                handleCategoryChange
                            }
                            labelKey="label"
                            valueKey="value"
                            size="small"
                            error={
                                !!errors?.category
                            }
                            errorMessage={
                                errors?.category
                            }
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Footer */}
            <Box
                display="flex"
                justifyContent="space-between"
                mt={4}
                pt={2.5}
                borderTop="1px solid"
                borderColor="grey.100"
            >
                <Button
                    variant="outlined"
                    startIcon={
                        <ArrowBackIcon />
                    }
                    onClick={onBack}
                    sx={{
                        borderRadius: 2,
                        textTransform:
                            "none",
                        fontWeight: 600,
                    }}
                >
                    Back
                </Button>

                <Button
                    variant="contained"
                    endIcon={
                        <ArrowForwardIcon />
                    }
                    onClick={handleNext}
                    disableElevation
                    sx={{
                        borderRadius: 2,
                        textTransform:
                            "none",
                        fontWeight: 600,
                        px: 3,
                    }}
                >
                    Next: Business Details
                </Button>
            </Box>
        </Box>
    );
}
